import { SubscriptionUpgradeType } from "@jammable/clientapi/src/payments";
import { Spinner } from "@jammable/ui-components/Loader";
import { useUpgradeOptions } from "./hooks/useUpgradeOptions";
import { formatCurrency } from "./utils";
import { Check } from "lucide-react";

interface UpgradeProps {
  type: SubscriptionUpgradeType;
  fallback?: () => void;
  callback?: () => void;
}

// Design language F, matching apps/web/components/pricing/PricingCard:
// Creator is the featured purple card with the lime button, Power the white
// card with a 2px line border and the ink button. No shadows or gradients.
const PLAN_CONFIG: Record<
  string,
  {
    badge: string;
    card: string;
    badgeClass: string;
    muted: string;
    strike: string;
    tick: string;
    btn: string;
    benefits: Array<{ title: string; included: boolean }>;
  }
> = {
  Pro: {
    badge: "Most popular",
    card: "tw-bg-jam-purple tw-text-white",
    badgeClass: "tw-bg-jam-lime tw-text-jam-ink",
    muted: "tw-text-[#E3D3F7]",
    strike: "tw-text-jam-soft",
    tick: "tw-text-jam-lime",
    btn: "tw-bg-jam-lime tw-text-jam-ink hover:tw-brightness-95",
    benefits: [
      { title: "Unlimited Covers", included: true },
      { title: "Full Voice Library", included: true },
      { title: "Credits Never Expire", included: true },
      { title: "Text to Speech", included: true },
      { title: "Duets", included: true },
      { title: "Train 25 Voices a Month", included: true },
      { title: "Create Shareable Videos", included: true },
    ],
  },
  Studio: {
    badge: "Best value",
    card: "tw-border-2 tw-border-solid tw-border-jam-line tw-bg-white tw-text-jam-ink",
    badgeClass: "tw-bg-jam-lime tw-text-jam-ink",
    muted: "tw-text-jam-muted",
    strike: "tw-text-jam-muted",
    tick: "tw-text-jam-purple",
    btn: "tw-bg-jam-ink tw-text-white hover:tw-bg-jam-deep",
    benefits: [
      { title: "Unlimited Covers", included: true },
      { title: "Full Voice Library", included: true },
      { title: "Credits Never Expire", included: true },
      { title: "Text to Speech", included: true },
      { title: "Duets", included: true },
      { title: "Train 100 Voices a Month", included: true },
      { title: "Create Shareable Videos", included: true },
      { title: "WAV Downloads", included: true },
      { title: "Mix Controls", included: true },
      { title: "Unlimited Video Coins", included: true },
    ],
  },
};

export function Upgrade({
  type = "Jammable Creator",
  fallback,
  callback,
}: UpgradeProps) {
  const {
    isLoadingPrices,
    isUpgrading,
    upgradeOfType,
    currency,
    handleUpgrade,
  } = useUpgradeOptions({ type, fallback, callback });

  if (isLoadingPrices || !upgradeOfType)
    return (
      <div className="tw-m-20 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4">
        <Spinner className="tw-border-4" size={5} />
      </div>
    );

  const { title, amount, discountedAmount, plan } = upgradeOfType;
  const displayAmount =
    discountedAmount && discountedAmount > 0 ? discountedAmount : amount;
  const config = PLAN_CONFIG[title] || PLAN_CONFIG.Pro;

  return (
    <section className="tw-mx-auto tw-max-w-sm tw-pt-4">
      <div
        className={`tw-relative tw-flex tw-flex-col tw-gap-4 tw-rounded-[32px] tw-p-[22px] md:tw-rounded-[36px] md:tw-p-7 ${config.card}`}
      >
        <span
          className={`tw-absolute tw--top-3.5 tw-left-[26px] tw-rounded-full tw-px-3.5 tw-py-1.5 tw-text-xs tw-font-extrabold ${config.badgeClass}`}
        >
          {config.badge}
        </span>

        <div>
          <h3 className="jam-display tw-m-0 tw-text-[24px] tw-font-extrabold tw-capitalize md:tw-text-[28px]">
            {title}
          </h3>
          <p className={`tw-m-0 tw-text-sm tw-font-semibold ${config.muted}`}>
            Upgrade your plan
          </p>
        </div>

        <div>
          <div className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-2">
            <span className="jam-display tw-text-[40px] tw-font-extrabold tw-leading-none tw-tracking-[-0.04em] md:tw-text-[48px]">
              {currency && formatCurrency(displayAmount!, currency!)}
            </span>
            {discountedAmount && discountedAmount > 0 && (
              <span
                className={`tw-text-base tw-font-bold tw-line-through ${config.strike}`}
              >
                {currency && formatCurrency(amount!, currency!)}
              </span>
            )}
            <span className={`tw-text-sm tw-font-bold ${config.muted}`}>
              /{plan.interval}
            </span>
          </div>
          <p
            className={`tw-m-0 tw-mt-1 tw-text-[13px] tw-font-bold ${config.muted}`}
          >
            {discountedAmount && discountedAmount > 0
              ? `First month only, then ${formatCurrency(amount!, currency!)}/mo`
              : `Billed ${plan.interval}ly`}
          </p>
        </div>

        <button
          onClick={handleUpgrade}
          disabled={isUpgrading}
          className={`tw-flex tw-h-[52px] tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-0 tw-text-base tw-font-extrabold tw-transition active:tw-scale-[0.98] disabled:tw-cursor-default ${config.btn}`}
        >
          {isUpgrading ? (
            <Spinner size={4} className="tw-border-3 tw-border-current" />
          ) : (
            "Upgrade"
          )}
        </button>

        <div className="tw-flex tw-flex-col tw-gap-2.5 tw-text-sm tw-font-bold">
          {config.benefits.map((benefit) => (
            <span
              key={benefit.title}
              className="tw-flex tw-items-start tw-gap-2"
            >
              <Check
                size={16}
                strokeWidth={3}
                className={`tw-mt-0.5 tw-flex-shrink-0 ${config.tick}`}
              />
              {benefit.title}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
